<template>
  <Card>
    <template #title>
      <div class="text-2xl">Step 2: Seller Creation</div>
      <div class="text-sm text-500 mt-2">
        Group: <strong>{{ groupName }}</strong>
      </div>
    </template>
    <template #content>
      <!-- Sellers List -->
      <div class="sellers-list">
        <SellerFormCard
          v-for="(seller, index) in sellers"
          :key="index"
          v-model="sellers[index]"
          :index="index"
          :can-remove="sellers.length > 1"
          :group-data="groupData"
          @remove="removeSeller(index)"
        />
      </div>

      <!-- Add Seller Button -->
      <div class="mt-4">
        <Button
          label="Add Another Seller"
          icon="pi pi-plus"
          outlined
          @click="addSeller"
        />
      </div>
    </template>

    <template #footer>
      <div class="flex justify-content-between gap-2">
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          outlined
          @click="$emit('back')"
        />
        <Button
          label="Next"
          icon="pi pi-arrow-right"
          iconPos="right"
          @click="handleNext"
        />
      </div>
    </template>
  </Card>
</template>

<script setup>
import { ref, defineProps, defineEmits } from "vue";
import { useToast } from "primevue/usetoast";
import { useStepValidation } from "./composables/useStepValidation";
import Card from "primevue/card";
import Button from "primevue/button";
import SellerFormCard from "./SellerFormCard.vue";

const props = defineProps({
  groupId: {
    type: Number,
    required: true,
  },
  groupName: {
    type: String,
    default: "",
  },
  groupData: {
    type: Object,
    default: null,
  },
  data: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["next", "back", "update:data"]);
const toast = useToast();

const createDefaultSeller = () => ({
  name: "",
  domain: "",
  password: "",
  seller_type: "audio",
  contract_type: "revshare",
  contract_value: 50,
  categories: [],
  show_ids_content: [],
  connatix_player_id: "", // For audio_video sellers
  connatix_player_share: 0.6, // Default 60% revenue share
  // Content fields
  content_type: "shows", // Default to shows
  clone_seller_id: null,
  playlists: null,
});

const normalizeSeller = (seller) => {
  const base = createDefaultSeller();
  return {
    ...base,
    ...seller,
    categories: [...(seller?.categories || base.categories)],
    show_ids_content: [...(seller?.show_ids_content || base.show_ids_content)],
  };
};

const sellers = ref(
  props.data.length > 0
    ? props.data.map((seller) => normalizeSeller(seller))
    : [createDefaultSeller()]
);

// Use validation composable (without passing data)
const { errors, validate } = useStepValidation(2);

const addSeller = () => {
  sellers.value.push(createDefaultSeller());
};

const removeSeller = (index) => {
  if (sellers.value.length > 1) {
    sellers.value.splice(index, 1);
  }
};

const handleNext = () => {
  // Prepare validation data
  const validationData = {
    sellers: sellers.value,
  };
  // Run validation with data
  const isValid = validate(validationData);

  if (!isValid) {
    // Show first validation error
    const firstError = Object.values(errors)[0];
    toast.add({
      severity: "warn",
      summary: "Validation Error",
      detail: firstError || "Please check all sellers",
      life: 3000,
    });
    return;
  }

  // Emit sellers data to parent
  emit("update:data", sellers.value);
  emit("next", sellers.value);
};
</script>

<style scoped>
.sellers-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
</style>
